import { TPresenceItem } from '@/types/presences';

type TGroupItemsByIdProps<T> = {
  items: T[];
  idKey: keyof T;
};

export type TGroupItemsByIdReturn<T> = Array<{ id: T[keyof T]; children: T[] }>;

const buildGroupedMap = <T>(items: T[], idKey: keyof T): Map<T[keyof T], T[]> => {
  const groupedMap = new Map<T[keyof T], T[]>();
  for (const item of items) {
    const id = item[idKey];
    if (!groupedMap.has(id)) {
      groupedMap.set(id, []);
    }
    groupedMap.get(id)!.push(item);
  }
  return groupedMap;
};

const groupItems = <T, R>(
  items: T[],
  idKey: keyof T,
  projector: (id: T[keyof T], children: T[]) => R
): R[] => {
  const groupedMap = buildGroupedMap(items, idKey);
  const result: R[] = [];
  groupedMap.forEach((children, id) => {
    result.push(projector(id, children));
  });
  return result;
};

export const groupItemsById = <T>({ items, idKey }: TGroupItemsByIdProps<T>) =>
  groupItems(items, idKey, (id, children) => ({ id, children }));

export const groupedItemsById = groupItemsById;

type GroupedByKeyword<T> = { id: string; children: T[] }[];

export function groupItemsByKeywords<T extends { keywords: string[] }>(
  items: T[]
): GroupedByKeyword<T> {
  const map = new Map<string, T[]>();

  items.forEach(item => {
    item.keywords.forEach(keyword => {
      if (!map.has(keyword)) {
        map.set(keyword, []);
      }
      map.get(keyword)!.push(item);
    });
  });

  return Array.from(map.entries()).map(([keyword, children]) => ({
    id: keyword,
    children,
  }));
}

const ENTITY_SCORE_VALUES = {
  High: 'Alta',
  Medium: 'Media',
  Low: 'Baja',
};

export const GROUPING_CRITERIA = {
  Reliability: 'entity_score',
  Support: 'support_id',
  Visibility: 'visibility',
  Logo: 'logo_id',
};

const resolveEntityScore = (score: number): string => {
  if (score >= 0.9) return ENTITY_SCORE_VALUES.High;
  if (score >= 0.5) return ENTITY_SCORE_VALUES.Medium;
  return ENTITY_SCORE_VALUES.Low;
};

type CriteriaKey = (typeof GROUPING_CRITERIA)[keyof typeof GROUPING_CRITERIA];

const getCriteriaValue = <T extends TPresenceItem>(item: T, criteria: CriteriaKey): string => {
  const UNKNOWN_VALUE = 'UNKNOWN';
  switch (criteria) {
    case GROUPING_CRITERIA.Reliability:
      return resolveEntityScore(item.entity_score);

    case GROUPING_CRITERIA.Support:
      return (item.parameters as any).support_id || UNKNOWN_VALUE;

    case GROUPING_CRITERIA.Visibility:
      return (item.parameters as any).visibility || UNKNOWN_VALUE;

    default:
      return UNKNOWN_VALUE;
  }
};

type PresenceWithCriteria<T extends TPresenceItem> = T & {
  __criteria: string;
};
export const groupItemsByCriteria = <T extends TPresenceItem>(
  items: T[],
  criteria: CriteriaKey
) => {
  const itemsWithCriteria: PresenceWithCriteria<T>[] = items.map(item => ({
    ...item,
    __criteria: getCriteriaValue<T>(item, criteria),
  }));
  return groupItems(itemsWithCriteria, '__criteria', (id, children) => ({
    id,
    children: children as T[],
  }));
};
